// public/js/features/tracker-config/battery-ui.js
// Collapsible battery-life panel. Shows a one-line estimate by default; the
// "Details & assumptions" expander reveals the trip/fix/conditions inputs, an
// optional (default-off) Bluetooth-scanning section, and a per-part energy
// breakdown. Pure UI over the model in battery.js.
//
// Customer-facing: the device is labelled by its Superb brand, never the vendor
// family. Callers pass `families` = [{key, label}] with brand labels; internal
// callers may omit it to fall back to raw vendor labels. See docs/battery-life.md.
import { estimateBatteryLife, formatLife, defaultAssumptions, BATTERY_FAMILIES, DEFAULT_FAMILY, supportsWifi } from './battery.js';
import { helpIcon, initHelp } from '../../ui/help.js';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const round1 = (n) => Math.round(n * 10) / 10;

const HELP = {
  'bat.family': 'The device model decides the energy cost per GPS fix, upload and Bluetooth scan.',
  'bat.tripsPerDay': 'How many separate journeys the asset makes per week on average. Each journey adds a burst of position fixes and uploads while moving.',
  'bat.tripLenMin': 'Average length of one journey in hours. Longer journeys mean more moving positions and uploads per trip.',
  'bat.positioning': 'How the tracker finds its position. Wi-Fi is tried first - it fixes faster and uses far less power where Wi-Fi networks are in range - and the tracker falls back to GPS when none are seen. GPS-only skips Wi-Fi. The time a GPS fix takes is a fixed assumption (about 30s), so there is no field for it.',
  'bat.conditionsPct': 'How much of the rated battery you realistically expect to use. Poor cellular coverage, cold temperature and roaming all cost extra energy. 100% = ideal lab conditions; ~85% is a sensible real-world default.',
  'bat.ble': 'When the tracker also scans for nearby Bluetooth tags and sensors it acts as a gateway. Scanning is power-hungry and shortens battery life a lot - turn it on only if this tracker relays tags.',
  'bat.bleInterval': 'How often a Bluetooth scan starts (seconds). Longer = less battery, but tags are noticed less often.',
  'bat.bleWindow': 'How long each Bluetooth scan lasts (seconds). Must be shorter than the interval; too short can miss a tag.',
};

// `disp` = multiplier from the internal value to the displayed value, so the
// model stays in per-day / minutes while the UI reads in per-week / hours.
const NUM = [
  { key: 'tripsPerDay', label: 'Trips per week', min: 0, max: 200, step: 1, disp: 7 },
  { key: 'tripLenMin', label: 'Avg trip length', unit: 'h', min: 0, max: 48, step: 1, disp: 1 / 60 },
  { key: 'conditionsPct', label: 'Usable capacity', unit: '%', min: 30, max: 100, step: 5, disp: 1 },
];

// createBatteryPanel(host, opts) -> { el, refresh(settings), setFamily(key) }
//   families        [{key,label}] shown in the picker (brand labels). Omit = vendor labels.
//   familyKey       initial selection (defaults to families[0]).
//   familyLocked    disable switching.
//   hideFamilyRow   do not render the model row at all (single fixed device).
//   hideNotes       suppress the technical caveat notes (vendor-named) on customer pages.
//   capacity        override battery capacity (mAh).
//   subtitle        override the small text after the headline.
//   assumptions     seed values for the trip/fix/conditions inputs.
//   conditionsLocked  render the "Usable capacity" (conditions) input read-only.
//   bleControls     add a collapsible, default-OFF Bluetooth-scanning section that
//                   overrides the BLE settings for the estimate (used on the manual
//                   page where there is no real config to read).
export function createBatteryPanel(host, opts = {}) {
  const { familyLocked = false, hideFamilyRow = false, hideNotes = false, capacity = null, subtitle = '',
    conditionsLocked = false, bleControls = false } = opts;
  const fams = Array.isArray(opts.families) && opts.families.length
    ? opts.families
    : Object.entries(BATTERY_FAMILIES).map(([key, f]) => ({ key, label: f.label }));
  const labelOf = (key) => fams.find(f => f.key === key)?.label || BATTERY_FAMILIES[key]?.label || key;

  const state = {
    familyKey: (opts.familyKey && fams.some(f => f.key === opts.familyKey)) ? opts.familyKey : (fams[0]?.key || DEFAULT_FAMILY),
    assumptions: { ...defaultAssumptions({}), ...(capacity ? { capacity } : {}), ...(opts.assumptions || {}) },
    ble: { enabled: false, intervalS: 3600, windowS: 35, ...(opts.ble || {}) },
    settings: {},
    open: false,
  };

  // Effective tracker settings for the estimate. When the panel owns BLE, it
  // overrides the incoming settings' Bluetooth fields with its own controls.
  const effSettings = () => bleControls
    ? { ...state.settings, bleScan: state.ble.enabled, bleScanIntervalS: state.ble.intervalS, bleScanWindowS: state.ble.windowS }
    : state.settings;

  host.innerHTML = `
    <div class="card border-0 bg-body-tertiary">
      <div class="card-body py-2 px-3">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <i class="fa-solid fa-battery-three-quarters me-2 text-success"></i>
            <span class="fw-semibold">Estimated battery life:</span>
            <span id="bat-life" class="fw-bold">-</span>
            <span id="bat-sub" class="text-muted small ms-1"></span>
          </div>
          <button type="button" id="bat-toggle" class="btn btn-sm btn-link text-decoration-none p-0">
            <i class="fa-solid fa-chevron-down me-1"></i>Details &amp; assumptions
          </button>
        </div>
        <div id="bat-details" class="mt-3" style="display:none;"></div>
      </div>
    </div>`;

  const lifeEl = host.querySelector('#bat-life');
  const subEl = host.querySelector('#bat-sub');
  const detailsEl = host.querySelector('#bat-details');
  const toggle = host.querySelector('#bat-toggle');

  toggle.addEventListener('click', () => {
    state.open = !state.open;
    detailsEl.style.display = state.open ? 'block' : 'none';
    toggle.querySelector('i').className = state.open ? 'fa-solid fa-chevron-up me-1' : 'fa-solid fa-chevron-down me-1';
    if (state.open) renderDetails();
  });

  const familyRow = () => hideFamilyRow ? '' : `
    <div class="mb-2">
      <label class="form-label small mb-1">Device${helpIcon('bat.family')}</label>
      <select id="bat-family" class="form-select form-select-sm" ${familyLocked ? 'disabled' : ''}>
        ${fams.map(f => `<option value="${esc(f.key)}"${state.familyKey === f.key ? ' selected' : ''}>${esc(f.label)}</option>`).join('')}
      </select>
    </div>`;

  // Positioning method (only for Wi-Fi-capable devices; GPS-only ones don't show it).
  const positioningRow = () => !supportsWifi(state.familyKey) ? '' : `
    <div class="mb-2">
      <label class="form-label small mb-1">Positioning${helpIcon('bat.positioning')}</label>
      <select id="bat-pos" class="form-select form-select-sm">
        <option value="wifi"${(state.assumptions.positioning || 'wifi') === 'wifi' ? ' selected' : ''}>Wi-Fi first (GPS fallback)</option>
        <option value="gps"${state.assumptions.positioning === 'gps' ? ' selected' : ''}>GPS only</option>
      </select>
    </div>`;

  const numRow = (n) => `
    <div class="mb-2 d-flex align-items-center gap-2">
      <label class="form-label small mb-0 flex-grow-1">${esc(n.label)}${helpIcon('bat.' + n.key)}</label>
      <div class="input-group input-group-sm" style="max-width:130px;">
        <input type="number" class="form-control bat-assume" data-key="${n.key}" data-disp="${n.disp}"
               value="${round1((state.assumptions[n.key] ?? 0) * n.disp)}" min="${n.min}" max="${n.max}" step="${n.step}"
               ${(n.key === 'conditionsPct' && conditionsLocked) ? 'disabled' : ''}>
        ${n.unit ? `<span class="input-group-text">${esc(n.unit)}</span>` : ''}
      </div>
    </div>`;

  const bleSection = () => !bleControls ? '' : `
    <div class="mt-3 pt-2 border-top">
      <div class="form-check form-switch mb-0">
        <input class="form-check-input" type="checkbox" id="bat-ble-toggle" ${state.ble.enabled ? 'checked' : ''}>
        <label class="form-check-label small fw-semibold" for="bat-ble-toggle">Bluetooth gateway scanning${helpIcon('bat.ble')}</label>
      </div>
      <div id="bat-ble-fields" class="mt-2 ps-4" style="display:${state.ble.enabled ? 'block' : 'none'};">
        <div class="mb-2 d-flex align-items-center gap-2">
          <label class="form-label small mb-0 flex-grow-1">Scan interval${helpIcon('bat.bleInterval')}</label>
          <div class="input-group input-group-sm" style="max-width:130px;">
            <input type="number" class="form-control bat-ble" data-key="intervalS" value="${state.ble.intervalS}" min="5" max="3600" step="5">
            <span class="input-group-text">s</span>
          </div>
        </div>
        <div class="mb-1 d-flex align-items-center gap-2">
          <label class="form-label small mb-0 flex-grow-1">Scan window${helpIcon('bat.bleWindow')}</label>
          <div class="input-group input-group-sm" style="max-width:130px;">
            <input type="number" class="form-control bat-ble" data-key="windowS" value="${state.ble.windowS}" min="1" max="300" step="1">
            <span class="input-group-text">s</span>
          </div>
        </div>
        <div class="small text-muted">Gateway scanning shortens battery life noticeably.</div>
      </div>
    </div>`;

  function renderDetails() {
    const est = estimateBatteryLife(effSettings(), state.assumptions, state.familyKey);
    const bd = est.breakdown;
    const rows = [
      ['Moving (trips)', bd.moving], ['Parked / heartbeat', bd.idle],
      ['Bluetooth scanning', bd.ble], ['Sleep', bd.sleep], ['Self-discharge', bd.selfDischarge],
    ].filter(r => r[1] > 0 || r[0] !== 'Bluetooth scanning');
    const total = est.mAhPerDay || 1;

    detailsEl.innerHTML = `
      <div class="row g-3">
        <div class="col-md-6">
          <div class="small fw-semibold text-secondary mb-2">Assumptions</div>
          ${familyRow()}
          ${positioningRow()}
          ${NUM.map(numRow).join('')}
          ${bleSection()}
        </div>
        <div class="col-md-6">
          <div class="small fw-semibold text-secondary mb-2">Daily energy (${round1(total)} mAh/day of ${Math.round(est.usableCapacity)} mAh usable)</div>
          ${rows.map(([label, v]) => {
            const pct = Math.min(100, (v / total) * 100);
            return `<div class="mb-1">
              <div class="d-flex justify-content-between small"><span>${esc(label)}</span><span class="text-muted">${v >= 0.01 ? round1(v) : v.toFixed(3)} mAh</span></div>
              <div class="progress" style="height:6px;"><div class="progress-bar bg-info" style="width:${pct}%"></div></div>
            </div>`;
          }).join('')}
          <div class="small text-muted mt-2">Estimate only - real battery life varies with coverage, temperature and how often the device reports.</div>
          ${(!hideNotes && est.notes.length) ? `<div class="alert alert-warning py-1 px-2 small mt-2 mb-0">${est.notes.map(esc).join('<br>')}</div>` : ''}
        </div>
      </div>`;

    detailsEl.querySelector('#bat-family')?.addEventListener('change', (e) => { state.familyKey = e.target.value; recompute(); renderDetails(); });
    detailsEl.querySelector('#bat-pos')?.addEventListener('change', (e) => { state.assumptions.positioning = e.target.value; recompute(); renderDetails(); });
    detailsEl.querySelectorAll('.bat-assume').forEach(inp => inp.addEventListener('input', () => {
      const disp = Number(inp.dataset.disp) || 1;
      state.assumptions[inp.dataset.key] = Number(inp.value) / disp;   // display -> internal
      recompute();
      clearTimeout(inp._t); inp._t = setTimeout(renderDetails, 250);
    }));
    detailsEl.querySelector('#bat-ble-toggle')?.addEventListener('change', (e) => {
      state.ble.enabled = e.target.checked;
      recompute(); renderDetails();
    });
    detailsEl.querySelectorAll('.bat-ble').forEach(inp => inp.addEventListener('input', () => {
      state.ble[inp.dataset.key] = Number(inp.value);
      recompute();
      clearTimeout(inp._t); inp._t = setTimeout(renderDetails, 250);
    }));
    initHelp(detailsEl, HELP);
  }

  function recompute() {
    const est = estimateBatteryLife(effSettings(), state.assumptions, state.familyKey);
    lifeEl.textContent = formatLife(est);
    lifeEl.className = 'fw-bold ' + (est.years >= 3 ? 'text-success' : est.years >= 1 ? 'text-warning' : 'text-danger');
    subEl.textContent = subtitle || `(${labelOf(state.familyKey)})`;
  }

  return {
    el: host,
    refresh(settings) { state.settings = settings || {}; recompute(); if (state.open) renderDetails(); },
    setFamily(key) { if (fams.some(f => f.key === key)) { state.familyKey = key; recompute(); if (state.open) renderDetails(); } },
  };
}
